/**
 * Stage delays (the user, 2026-10-08): "sometimes a stage runs late and
 * everything is pushed by X minutes" — and a phone in a field cannot ask the
 * server, so the person can say so themselves.
 *
 * A delay names the SET it starts from (`fromActId`, the set on that stage at
 * the moment somebody said "running late") and how many minutes. That set
 * moves by the full delay; each set after it on the same stage moves only as
 * far as it must not to overlap the one before — so a break between sets
 * absorbs the delay, and the first set that no longer collides keeps its
 * time, with everything after it. 12:00–22:00 then 00:00–05:00, 30 minutes
 * late from 15:00: the run ends at 22:30, and 00:00 starts at 00:00.
 *
 * Pure, and the same here, in the API (reminders, notifications) and on the
 * phone (every screen), so a shifted set is shifted the same everywhere.
 */

export interface StageDelay {
  stageId: string;
  /** The set the delay starts from. */
  fromActId: string;
  minutes: number;
}

/** How many different people must report the SAME minutes for the same set before everyone's lineup moves. */
export const DELAY_REPORTS_TO_SHIFT = 10;
/** The longest delay anybody may report. */
export const MAX_DELAY_MINUTES = 240;

export function isDelayMinutes(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= MAX_DELAY_MINUTES;
}

export interface Shifted { startAt: Date; endAt: Date; delayMs: number }

/**
 * Every set the delays move, by id: its new start and end, and by how much.
 * Sets no delay reaches are absent. Delays on one stage apply in the order of
 * the sets they start from, each on top of the last.
 */
export function applyStageDelays(
  acts: readonly { id: string; stageId: string; startAt: Date | string; endAt: Date | string }[],
  delays: readonly StageDelay[],
): Map<string, Shifted> {
  const out = new Map<string, Shifted>();
  if (delays.length === 0) return out;
  const ms = (d: Date | string) => (d instanceof Date ? d.getTime() : Date.parse(d));
  const byStage = new Map<string, { id: string; start: number; end: number }[]>();
  for (const a of acts) {
    const list = byStage.get(a.stageId) ?? [];
    list.push({ id: a.id, start: ms(a.startAt), end: ms(a.endAt) });
    byStage.set(a.stageId, list);
  }
  for (const list of byStage.values()) list.sort((x, y) => x.start - y.start || x.id.localeCompare(y.id));

  const current = (id: string, start: number, end: number) => {
    const s = out.get(id);
    return s ? { start: s.startAt.getTime(), end: s.endAt.getTime() } : { start, end };
  };

  const ordered = [...delays]
    .filter((d) => isDelayMinutes(d.minutes))
    .map((d) => ({ d, at: byStage.get(d.stageId)?.find((a) => a.id === d.fromActId)?.start ?? Infinity }))
    .sort((x, y) => x.at - y.at);

  for (const { d } of ordered) {
    const list = byStage.get(d.stageId);
    const from = list?.findIndex((a) => a.id === d.fromActId) ?? -1;
    if (!list || from < 0) continue;
    let prevEnd = -Infinity;
    for (let i = from; i < list.length; i++) {
      const orig = list[i]!;
      const now = current(orig.id, orig.start, orig.end);
      const length = now.end - now.start;
      const start = i === from ? now.start + d.minutes * 60_000 : Math.max(now.start, prevEnd);
      // No longer pushed: this set and every one after it keep their times.
      if (i > from && start <= now.start) break;
      const end = start + length;
      out.set(orig.id, { startAt: new Date(start), endAt: new Date(end), delayMs: start - orig.start });
      prevEnd = end;
    }
  }
  return out;
}

/** The set on a stage at `at` — the one a "running late" report starts from — else the next one there. */
export function delayAnchor(
  acts: readonly { id: string; stageId: string; startAt: Date | string; endAt: Date | string }[],
  stageId: string, at: Date,
): string | null {
  const ms = (d: Date | string) => (d instanceof Date ? d.getTime() : Date.parse(d));
  const t = at.getTime();
  const here = acts.filter((a) => a.stageId === stageId).sort((x, y) => ms(x.startAt) - ms(y.startAt));
  return (here.find((a) => ms(a.startAt) <= t && t < ms(a.endAt)) ?? here.find((a) => ms(a.startAt) > t))?.id ?? null;
}
